import { multiplyMatrix, solve6LinearEquationByMatrix } from "./math";

// the Earth radius in m
export const EARTH_RADIUS = 6378137;

export interface GeoPoint {
  longitude: number,
  latitude: number,
}

export function radiansToDegree(rad: number) {
  return rad * (180 / Math.PI);
}

export function degreeToRadians(deg: number) {
  return deg * (Math.PI / 180);
}

interface RelativePoint {
  x: number,
  y: number,
}

interface MapPoint {
  geoPoint: {
    latitude: string,
    longitude: string,
  },
  mapPoint: RelativePoint,
}

export interface MapState {
  firstPoint: MapPoint,
  secondPoint: MapPoint,
  thirdPoint: MapPoint,
}

export function getDistanceBetweenTwoGeoPoints(geoPoint1: GeoPoint, geoPoint2: GeoPoint) {
  const longitude1 = geoPoint1.longitude || 0;
  const latitude1 = geoPoint1.latitude || 0;
  const longitude2 = geoPoint2.longitude || 0;
  const latitude2 = geoPoint2.latitude || 0;

  const dLat = degreeToRadians(latitude2 - latitude1);
  const dLon = degreeToRadians(longitude2 - longitude1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(degreeToRadians(latitude1)) *
    Math.cos(degreeToRadians(latitude2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return c * EARTH_RADIUS;
}

export function convertStringGeoCoordinateToNumber(point: { longitude: string, latitude: string }) {
  return {
    longitude: parseFloat(point.longitude),
    latitude: parseFloat(point.latitude),
  }
}

export function calculateNewRelativePointCoordinates(geoPoint: GeoPoint, mapState: MapState) {
  const matrix = getAffinisMatrix(
    [ convertStringGeoCoordinateToNumber(mapState.firstPoint.geoPoint), convertStringGeoCoordinateToNumber(mapState.secondPoint.geoPoint), convertStringGeoCoordinateToNumber(mapState.thirdPoint.geoPoint) ],
    [ mapState.firstPoint.mapPoint, mapState.secondPoint.mapPoint, mapState.thirdPoint.mapPoint ]
  );

  const result = multiplyMatrix(matrix, [ [ geoPoint.latitude ], [ geoPoint.longitude ], [ 1 ] ]);

  return {
    x: result[0][0],
    y: result[1][0],
  };
}

export function calculateNewPathRelativePointCoordinates(path: string, mapState: MapState) {
  const pathPointList = JSON.parse(path) as unknown as GeoPoint[];

  const newPointList = pathPointList.map((point) => calculateNewRelativePointCoordinates(point, mapState));

  return JSON.stringify(newPointList);
}

export function getAffinisMatrix(geoPointList: [ GeoPoint, GeoPoint, GeoPoint ], relativePointList: [ RelativePoint, RelativePoint, RelativePoint ]) {
  const resultMatrix = solve6LinearEquationByMatrix(
    [
      [ geoPointList[0].latitude, geoPointList[0].longitude, 1, 0, 0, 0 ],
      [ geoPointList[1].latitude, geoPointList[1].longitude, 1, 0, 0, 0 ],
      [ geoPointList[2].latitude, geoPointList[2].longitude, 1, 0, 0, 0 ],
      [ 0, 0, 0, geoPointList[0].latitude, geoPointList[0].longitude, 1 ],
      [ 0, 0, 0, geoPointList[1].latitude, geoPointList[1].longitude, 1 ],
      [ 0, 0, 0, geoPointList[2].latitude, geoPointList[2].longitude, 1 ],
    ],
    [
      relativePointList[0].x,
      relativePointList[1].x,
      relativePointList[2].x,
      relativePointList[0].y,
      relativePointList[1].y,
      relativePointList[2].y,
    ]
  );

  return [
    [ resultMatrix[0][0], resultMatrix[1][0], resultMatrix[2][0] ],
    [ resultMatrix[3][0], resultMatrix[4][0], resultMatrix[5][0] ],
    [ 0, 0, 1 ],
  ]
}